import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouterLegacy } from 'core/routerUtils';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as staticActions from 'shared/ducks/staticDuck';
import * as materialChainActions from 'features/material-chain/duck';
import { mapRoomsByLocation } from 'features/tc-event-dashboard/selectors';
import {
  AlertMessage,
  AlertNotice,
  Spinner,
  TooltipItem,
  EventDashboardDetails,
} from 'shared/components';
import ArrowRight from 'assets/svgs/arrowRight24.svg';
import moment from 'moment';
import { isEmpty, sortBy, padStart, findIndex } from 'lodash';
import { ROOM_STATUS } from 'shared/constants';
import ClockSmall from 'assets/svgs/clockSmall.svg';
import PauseSmall from 'assets/svgs/pauseSmall.svg';
import ClockIcon from 'assets/svgs/clockIcon.svg';
import PauseIcon from 'assets/svgs/pauseIcon.svg';
import LaptopIcon from 'assets/svgs/laptopIcon.svg';
import PaperIcon from 'assets/svgs/paperIcon.svg';

import './RoomsDashboard.scss';

const REFRESH_FREQUENCY_SEC = 30;

const getActiveTimerTime = (roomDTO) => {
  const activePause = roomDTO.currentPauseStartDateTime && !roomDTO.currentUnpauseStartDateTime;
  const referenceDatetime = activePause
    ? moment(roomDTO.currentPauseStartDateTime)
    : moment(roomDTO.currentTime);
  const calculateTimerTime = moment(roomDTO.activeRoomTimerStartDateTime)
    .add(roomDTO.timerDuration, 'minutes')
    .add(roomDTO.activeRoomTimerTotalPauseTime, 'seconds')
    .add(activePause, 'miliseconds');
  if (roomDTO.timerDurationSeconds && roomDTO.timerDurationSeconds > 0) {
    calculateTimerTime.add(roomDTO.timerDurationSeconds, 'seconds');
  }
  return calculateTimerTime.diff(referenceDatetime);
};

const sortRoomsDTO = (roomsDTO) => {
  const data = {};
  if (roomsDTO?.length) {
    roomsDTO.forEach((room) => {
      data[room.id] = {
        ...room,
        irregularitiesCount:
          (room.reviewedIrregularityCount || 0) + (room.submittedIrregularityCount || 0),
        activeTimerTime: room.activeRoomTimerId ? getActiveTimerTime(room) : null,
      };
    });
  }

  return data;
};

const filterRoomTasks = (tasks, rooms) => {
  const roomsTasks = {};
  if (rooms && rooms.length) {
    rooms.forEach((room) => {
      roomsTasks[room.id] = !isEmpty(room.roomType)
        ? sortBy(
            tasks.filter(
              (t) => t.roomType.name === room.roomType.name && t.roomTiming === room.timing
            ),
            ['displayOrder']
          )
        : null;
    });
  }
  return roomsTasks;
};

const findTaskIndex = (tasks, roomDTO) => {
  let taskIndex = 0;
  if (!isEmpty(tasks) && !isEmpty(roomDTO)) {
    taskIndex = findIndex(tasks, { id: roomDTO.lastCompletedTaskId });
  }

  return taskIndex;
};

const formatTime = (diffTime) => {
  const duration = moment.duration(Math.abs(diffTime));

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds();

  return `${padStart(hours, 2, '0')}:${padStart(minutes, 2, '0')}:${padStart(seconds, 2, '0')}`;
};

class RoomsDashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      countSeconds: 0,
      roomsDTO: {},
      roomAvailableTasks: {},
      loadingData: false,
    };

    this.handleRefresh = this.handleRefresh.bind(this);
    this.removeMessage = this.removeMessage.bind(this);
    this.onInterval = this.onInterval.bind(this);
  }

  componentDidMount() {
    const { actions, tcEvent, testCenterCycle } = this.props;
    const { eventId } = this.props.match.params;

    if (!isEmpty(tcEvent)) {
      actions.getTasks(tcEvent.testingEvent.adminId.cycleId.id);
      this.handleRefresh(false).then(() => {
        this.intervalId = setInterval(this.onInterval, 1000);
      });
      if (isEmpty(testCenterCycle)) {
        actions.getTestCenterCycle(eventId);
      }
    } else {
      this.handleRefresh(false).then(() => {
        this.intervalId = setInterval(this.onInterval, 1000);
      });
      actions.getTcEvent(eventId).then(() => {
        actions.getTasks(this.props.tcEvent.testingEvent.adminId.cycleId.id);
        actions.getTestCenterCycle(eventId);
      });
    }
  }

  componentDidUpdate(prevProps) {
    let roomsDTO = {};
    const roomAvailableTasks = {};

    if (
      prevProps.locations !== this.props.locations ||
      prevProps.tasks !== this.props.tasks ||
      prevProps.roomsDTO !== this.props.roomsDTO
    ) {
      this.props.locations.map((location) => {
        Object.assign(roomAvailableTasks, filterRoomTasks(this.props.tasks, location.rooms));
      });

      Object.assign(roomsDTO, sortRoomsDTO(this.props.roomsDTO));

      this.setState({
        roomsDTO,
        roomAvailableTasks,
      });
    }
  }

  async handleRefresh(flagRefreshButton) {
    const { actions } = this.props;
    const { eventId } = this.props.match.params;

    this.setState({
      loadingData: true,
    });

    await actions.getRooms(eventId);
    await actions.getRoomsDTO(eventId);
    await actions.getTCEventMaterialTypes(eventId);
    await actions.getRoomMaterialsCount(eventId);
    await actions.getEventAllMaterials(eventId);

    this.setState({
      loadingData: false,
    });

    if (flagRefreshButton) {
      this.setState({
        showMessage: true,
        showMessageTime: 3000,
        countSeconds: 0,
      });
    }
  }

  onInterval() {
    this.setState((prevState) => ({
      countSeconds: prevState.countSeconds + 1,
    }));
  }

  removeMessage() {
    this.setState({
      showMessage: false,
    });
  }

  componentWillUnmount() {
    clearInterval(this.intervalId);
  }

  render() {
    const { tcEvent, testCenterCycle, locations, loadingRooms } = this.props;

    const { roomsDTO, roomAvailableTasks, loadingData, countSeconds } = this.state;

    if (loadingRooms || isEmpty(tcEvent) || isEmpty(testCenterCycle)) {
      return <Spinner />;
    }

    const rsMappedText = {
      [ROOM_STATUS.submitted]: 'Submitted',
      [ROOM_STATUS.closed]: 'Closed',
      [ROOM_STATUS.opened]: 'Open',
      [ROOM_STATUS.inactive]: 'Unopened',
    };

    return (
      <div
        className={
          loadingData ? 'tc-event-rooms-dashboard loading-data' : 'tc-event-rooms-dashboard'
        }>
        <div className="mb-30 title-container position-relative">
          <h4 className="">{this.props.t('testCenter.roomsDashboard.title')}</h4>
          <EventDashboardDetails
            date={tcEvent.testingEvent.name}
            name={
              testCenterCycle.testCenterProgram.testCenter &&
              testCenterCycle.testCenterProgram.testCenter.name
            }
          />
        </div>
        {locations.length &&
        locations.some((location) => location.rooms && location.rooms.length) ? (
          <div>
            <div>
              <AlertNotice
                message={this.props.t('testCenter.roomsDashboard.infoMessage')}
                inline={true}
              />
              <div className="refresh-btn">
                <button
                  className="btn btn-small btn-action"
                  disabled={countSeconds < REFRESH_FREQUENCY_SEC}
                  onClick={() => this.handleRefresh(true)}>
                  <i className="fa fa-repeat" />{' '}
                  {loadingData ? (
                    <span>
                      {this.props.t('testCenter.roomsDashboard.refreshingButton')}
                      ...
                    </span>
                  ) : (
                    this.props.t('testCenter.roomsDashboard.refreshButton')
                  )}
                </button>
                {countSeconds < REFRESH_FREQUENCY_SEC && (
                  <div className="wait-message">
                    {this.props.t('testCenter.roomsDashboard.waitMessage', {
                      sec: REFRESH_FREQUENCY_SEC - countSeconds,
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="table-details">
              <div>{this.props.t('testCenter.roomsDashboard.clickRoomMessage')}</div>
              <div>
                <i className="fa fa-repeat" />{' '}
                {this.props.t('testCenter.roomsDashboard.updatedLabel', {
                  minutes: Math.floor(countSeconds / 60),
                })}
              </div>
            </div>

            {locations.map((location) => (
              <div className="rooms-info" key={location.id}>
                <div className="location">
                  {location.name}
                  {location.area && location.area.length > 0 && `, ${location.area}`}
                </div>
                {location.rooms && !!location.rooms.length ? (
                  <div>
                    <div className="rooms-info-header">
                      <div className="roomCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.roomCol')}
                      </div>
                      <div className="roomStatusCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.roomStatusCol')}
                      </div>
                      <div className="timerCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.timerCol')}
                      </div>
                      <div className="taskCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.taskCol')}
                      </div>
                      <div className="roomTypeCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.roomTypeCol')}
                      </div>
                      <div className="roomTypeCol roomTiming">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.timingCol')}
                      </div>
                      <div className="irregularityCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.irregularityCol')}
                      </div>
                      <div className="supervisorCol">
                        {this.props.t('testCenter.roomsDashboard.roomsInfo.supervisorCol')}
                      </div>
                    </div>

                    {location.rooms.map((room) => (
                      <div className="room" key={room.id}>
                        <div className="roomCol">
                          <Link
                            className="room-name"
                            to={loadingData ? '' : `/test-event/${tcEvent.id}/room/${room.id}`}
                            href={loadingData ? '' : `/test-event/${tcEvent.id}/room/${room.id}`}>
                            <TooltipItem item={room.name} />
                          </Link>
                        </div>

                        <div className="roomStatusCol">
                          <span className="subtitle hide-desktop">
                            {this.props.t('testCenter.roomsDashboard.roomsInfo.roomStatusCol')}{' '}
                          </span>
                          <span>
                            {room.confirmMaterialDateTime
                              ? rsMappedText[ROOM_STATUS.submitted]
                              : rsMappedText[room.status]}
                          </span>
                        </div>

                        <div className="timerCol">
                          <span className="hide-tablet">
                            {roomsDTO[room.id] && roomsDTO[room.id].activeRoomTimerIsPaused ? (
                              <PauseSmall />
                            ) : (
                              <ClockSmall />
                            )}{' '}
                          </span>
                          <span className="hide-desktop hide-mobile">
                            {roomsDTO[room.id] && roomsDTO[room.id].activeRoomTimerIsPaused ? (
                              <PauseIcon />
                            ) : (
                              <ClockIcon />
                            )}{' '}
                          </span>
                          {roomsDTO[room.id] && roomsDTO[room.id].activeTimerTime ? (
                            <span>
                              {roomsDTO[room.id].activeRoomTimerIsPaused && (
                                <span>
                                  {this.props.t('testCenter.roomsDashboard.roomsInfo.paused')}
                                  <br />
                                </span>
                              )}
                              <span
                                className={
                                  roomsDTO[room.id].activeTimerTime < 0 ? 'overdue' : null
                                }>
                                {formatTime(roomsDTO[room.id].activeTimerTime)}{' '}
                                {roomsDTO[room.id].activeTimerTime < 0
                                  ? this.props.t('taskManagement.overdueLabel')
                                  : this.props.t('testCenter.roomsDashboard.leftLabel')}
                              </span>
                              <span className="fw-normal">
                                {' '}
                                - {roomsDTO[room.id].activeRoomTimerStartName}
                              </span>
                            </span>
                          ) : (
                            <span>
                              {roomsDTO[room.id] && roomsDTO[room.id].manualTimerFlag
                                ? this.props.t('taskManagement.manualTimer.manualTiming')
                                : this.props.t(
                                    'testCenter.roomsDashboard.roomsInfo.notActiveLabel'
                                  )}
                            </span>
                          )}
                        </div>

                        <div className="taskCol">
                          <div>
                            <span className="subtitle hide-desktop">
                              {this.props.t('testCenter.roomsDashboard.roomsInfo.taskCol')}{' '}
                            </span>
                            <span>
                              {roomAvailableTasks[room.id] &&
                                (roomsDTO[room.id]?.lastCompletedTaskName
                                  ? findTaskIndex(roomAvailableTasks[room.id], roomsDTO[room.id]) +
                                    1
                                  : 0)}{' '}
                              {this.props.t('testCenter.roomsDashboard.roomsInfo.ofLabel')}{' '}
                              {roomAvailableTasks[room.id]?.length}
                            </span>
                          </div>
                        </div>

                        <div className="roomTypeCol">
                          <span className="subtitle hide-desktop">
                            {this.props.t('testCenter.roomsDashboard.roomsInfo.roomTypeCol')}{' '}
                          </span>
                          <span>
                            {!room.roomType ? (
                              ''
                            ) : room.roomType.onlineFlag ? (
                              <LaptopIcon />
                            ) : (
                              <PaperIcon />
                            )}
                          </span>
                          {room.roomType
                            ? room.roomType.name
                            : this.props.t('common.labels.unknown')}
                        </div>

                        <div className="roomTypeCol">
                          <span className="subtitle hide-desktop">
                            {this.props.t('testCenter.roomsDashboard.roomsInfo.timingCol')}{' '}
                          </span>
                          <div>
                            {room.timing && !isEmpty(this.props.mappedRoomTiming)
                              ? this.props.mappedRoomTiming[room.timing]?.name
                              : this.props.t('common.labels.unknown')}
                          </div>
                        </div>

                        <div className="irregularityCol">
                          {roomsDTO && roomsDTO[room.id] && (
                            <div>
                              <div className="subtitle hide-desktop">
                                {this.props.t(
                                  'testCenter.roomsDashboard.roomsInfo.irregularityCol'
                                )}{' '}
                              </div>
                              <Link
                                to={
                                  loadingData ? '' : `/irregularities/all/${tcEvent.id}/${room.id}`
                                }
                                href={
                                  loadingData ? '' : `/irregularities/all/${tcEvent.id}/${room.id}`
                                }>
                                {this.props.t(
                                  'testCenter.roomsDashboard.roomsInfo.irregularitiesCount',
                                  {
                                    count: roomsDTO[room.id].irregularitiesCount,
                                  }
                                )}
                              </Link>
                            </div>
                          )}
                        </div>

                        <div className="supervisorCol">
                          <div className="subtitle hide-desktop">
                            {this.props.t('testCenter.roomsDashboard.roomsInfo.supervisorCol')}{' '}
                          </div>
                          {roomsDTO[room.id] && roomsDTO[room.id].roomSupervisor
                            ? roomsDTO[room.id].roomSupervisor
                            : this.props.t('testCenter.roomsDashboard.roomsInfo.noSupervisor')}
                        </div>

                        <button
                          className="hide-desktop btn btn-small room-tasks-btn"
                          onClick={() => {
                            this.props.history.push(`/test-event/${tcEvent.id}/room/${room.id}`);
                          }}>
                          {this.props.t('testCenter.roomsDashboard.roomTasksLable')}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="room">
                    <AlertNotice
                      message={this.props.t('testCenter.roomsDashboard.noRoomsCreated')}
                      inline={true}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div>
              <AlertNotice
                message={this.props.t('testCenter.roomsDashboard.noLocationsCreated')}
                inline={true}
              />
            </div>

            <Link
              to={`/test-event/${tcEvent.id}/rooms/information`}
              href={`/test-event/${tcEvent.id}/rooms/information`}
              className="btn-link icon-link text-strong">
              <ArrowRight />
              {this.props.t('roomBuilder.createNewRoomLabel')}
            </Link>
          </div>
        )}

        <button className="btn btn-outline ml-0 mt-3" onClick={this.props.history.goBack}>
          {this.props.t('common.labels.back')}
        </button>

        <AlertMessage
          show={this.state.showMessage}
          time={this.state.showMessageTime}
          removeMessage={this.removeMessage}
          message={this.props.t('testCenter.roomsDashboard.successMessage')}
        />
      </div>
    );
  }
}

RoomsDashboard.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  t: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    rooms: state.tcEvent.rooms || [],
    roomsDTO: state.tcEvent.roomsDTO,
    loadingRoomsDTO: state.tcEvent.loadingRoomsDTO,
    loadingRooms: state.tcEvent.loadingRooms,
    tasks: state.taskManagement.tasks,
    tcEvent: state.tcEvent.currentEvent || {},
    testCenterCycle: state.tcEvent.testCenterCycle || {},
    locations: mapRoomsByLocation(state),
    mappedRoomTiming: state.statics.mappedRoomTiming,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        ...tcEventActions,
        ...staticActions,
        ...taskActions,
        ...materialChainActions,
      },
      dispatch
    ),
  };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(RoomsDashboard))
);
